import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FileCheck, RefreshCw } from 'lucide-react';
import { toast } from '../../lib/safeToast';

const statusBadge = (status) => {
  switch (status) {
    case 'approved': return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Approved</Badge>;
    case 'co_recommended_approve': return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Compliance Approved</Badge>;
    case 'co_recommended_reject': return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Compliance Rejected</Badge>;
    case 'in_review': return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">In Review</Badge>;
    case 'info_requested': return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Info Requested</Badge>;
    case 'rejected': return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Rejected</Badge>;
    default: return <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">Pending</Badge>;
  }
};

export default function BackOfficeKyc() {
  const { api } = useAuth();
  const { isLive } = useEnvironment();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/merchants?per_page=100');
      setData(res.data?.data || []);
    } catch { toast.error('Failed to load applications'); setData([]); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData, isLive]);

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2"><FileCheck className="w-7 h-7 text-[#0B6445]" />All KYC Applications</h1>
            <p className="text-[#0E1B16]/70 mt-2">All merchant applications with KYC status overview.</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        </div>
        <Card className="border-[#DCE4E0] shadow-sm">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
            ) : data.length === 0 ? (
              <p className="text-center py-12 text-[#0E1B16]/60">No applications.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#DCE4E0]">
                    <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Name</th>
                    <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Email</th>
                    <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">KYC Status</th>
                    <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Onboarding</th>
                  </tr></thead>
                  <tbody>
                    {data.map((row) => (
                      <tr key={row.id} className="border-b border-[#DCE4E0]/80 hover:bg-[#F8FAFC]">
                        <td className="py-3 px-2 font-medium">{row.name || '—'}</td>
                        <td className="py-3 px-2">{row.email}</td>
                        <td className="py-3 px-2">{statusBadge(row.kyc_status)}</td>
                        <td className="py-3 px-2">{row.onboarding_complete ? 'Complete' : 'Incomplete'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
}
